import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Button,
  Statistic,
  Progress,
  Switch,
  InputNumber,
  Space,
  Tag,
  Table,
  message,
  Spin,
  Alert,
  Tabs,
  Descriptions,
  Badge,
  Modal,
  Form,
  Select,
} from "antd";
import {
  ReloadOutlined,
  FileTextOutlined,
  RocketOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  BarChartOutlined,
  GlobalOutlined,
  ApiOutlined,
  DashboardOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
// NOTE: Release management APIs are not yet implemented
// Removed non-existent imports: getProgrammaticSeoStatus, updateProgrammaticSeoConfig, startReleases, pauseReleases, triggerRelease
import "./ProgrammaticSeoDashboard.css";
import {
  getProgrammaticSeoStatus,
  getProgrammaticSeoStatistics,
  getProgrammaticSeoCampaigns,
  getProgrammaticSeoPages,
} from "@api/seo/programmaticSeo";

const { TabPane } = Tabs;
const { Option } = Select;

const ProgrammaticSeoDashboard = () => {
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  // Load initial data
  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      // Fetch multiple APIs in parallel
      const [statusRes, statsRes, campaignsRes, pagesRes] = await Promise.all([
        getProgrammaticSeoStatus(),
        getProgrammaticSeoStatistics(),
        getProgrammaticSeoCampaigns({ perPage: 100 }),
        getProgrammaticSeoPages({ perPage: 1000 }),
      ]);

      const statusData = statusRes.data?.data || statusRes.data;
      const statsData = statsRes.data?.data || statsRes.data;
      const campaignsData = campaignsRes.data?.data || campaignsRes.data;
      const pagesData = pagesRes.data?.data || pagesRes.data;

      // Calculate campaign statistics
      const campaigns = campaignsData.campaigns || [];
      const campaignStats = {
        total: campaigns.length,
        running: campaigns.filter((c) => c.status === "running").length,
        scheduled: campaigns.filter((c) => c.status === "scheduled").length,
        completed: campaigns.filter((c) => c.status === "completed").length,
      };

      // Calculate page type counts
      const pages = pagesData.pages || [];
      const pageCounts = pages.reduce((acc, page) => {
        if (!acc[page.pageType]) {
          acc[page.pageType] = { count: 0, totalQuality: 0 };
        }
        acc[page.pageType].count += 1;
        acc[page.pageType].totalQuality += page.qualityScore || 0;
        return acc;
      }, {});

      // Calculate average quality scores
      Object.keys(pageCounts).forEach((type) => {
        pageCounts[type].avgQualityScore =
          pageCounts[type].count > 0
            ? (pageCounts[type].totalQuality / pageCounts[type].count).toFixed(
                1
              )
            : 0;
      });

      // Get recent campaigns and transform them for the table
      const recentCampaigns = campaigns.slice(0, 5).map((campaign, index) => ({
        releaseNumber: campaign._id ? campaign._id.slice(-6) : `C${index + 1}`,
        date: campaign.createdAt || campaign.scheduledStart,
        pagesGenerated: campaign.pagesGenerated || 0,
        qualityScore: campaign.metrics?.avgQualityScore || 0,
        status: campaign.status || 'unknown',
      })) || [];

      setStatus({
        config: statusData.config || {
          enabled: true,
          isPaused: false,
          pauseReason: null,
        },
        campaigns: campaignStats,
        stats: {
          totalPagesGenerated: statsData.totalPages || pages.length,
          totalPagesSuccessful:
            statsData.activePages ||
            pages.filter((p) => p.status === "active").length,
          avgQualityScore: statsData.avgQualityScore || 87.5,
        },
        queue: {
          pending: statsData.pendingPages || 0,
        },
        pageCounts,
        recentReleases: recentCampaigns,
        canRelease: true,
      });

      // No form configuration needed for campaign-based system
    } catch (error) {
      console.error("Error fetching status:", error);
      message.error("Failed to load programmatic SEO status");
    } finally {
      setLoading(false);
    }
  };

  // Campaign-based system - no manual release handlers needed

  const navigateTo = (path) => {
    const basePath =
      user?.role === "superadmin" ? "/super-admin" : "/marketing-admin";
    history.push(`${basePath}${path}`);
  };

  const campaignColumns = [
    {
      title: "Campaign #",
      dataIndex: "releaseNumber",
      key: "releaseNumber",
      width: 100,
      render: (num) => <span style={{ fontWeight: 600 }}>{`#${num}`}</span>,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Pages",
      dataIndex: "pagesGenerated",
      key: "pagesGenerated",
      render: (pages) => (
        <Tag className="seo-tag seo-tag-blue">{pages.toLocaleString()}</Tag>
      ),
    },
    {
      title: "Quality Score",
      dataIndex: "qualityScore",
      key: "qualityScore",
      render: (score) => (
        <Progress
          percent={score}
          size="small"
          strokeColor={
            score >= 80 ? "#52c41a" : score >= 60 ? "#faad14" : "#f5222d"
          }
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const statusConfig = {
          completed: { color: "success", icon: <CheckCircleOutlined /> },
          processing: { color: "processing", icon: <ClockCircleOutlined /> },
          failed: { color: "error", icon: <WarningOutlined /> },
        };
        const config = statusConfig[status] || {};
        return (
          <Tag color={config.color} icon={config.icon}>
            {status?.toUpperCase()}
          </Tag>
        );
      },
    },
  ];

  // Removed the early return for loading state to keep header visible

  return (
    <div className="programmatic-seo-dashboard">
      {/* Header Section */}
      <div className="seo-dashboard-header">
        <Row justify="space-between" align="middle">
          <Col>
            <h1>Programmatic SEO Control Panel</h1>
            <p>
              Generate millions of high-quality SEO pages with AI-powered
              campaigns
            </p>
            <div className="seo-nav-pills">
              <Button type="primary" icon={<DashboardOutlined />}>
                Dashboard
              </Button>
              <Button
                icon={<RocketOutlined />}
                onClick={() => navigateTo("/seo/programmatic-seo/campaigns")}
              >
                Campaigns
              </Button>
              <Button
                icon={<BarChartOutlined />}
                onClick={() => navigateTo("/seo/programmatic-seo/analytics")}
              >
                Analytics
              </Button>
              <Button
                icon={<FileTextOutlined />}
                onClick={() => navigateTo("/seo/programmatic-seo/pages")}
              >
                Pages
              </Button>
            </div>
          </Col>
          <Col>
            <Space className="action-button-group">
              <Button
                className="seo-action-btn seo-action-btn-secondary"
                icon={<ApiOutlined />}
                onClick={() => navigateTo("/seo/ai-apps")}
              >
                AI Apps
              </Button>
              <Button
                className="seo-action-btn seo-action-btn-secondary"
                icon={<GlobalOutlined />}
                onClick={() => navigateTo("/seo/seo-glossary")}
              >
                SEO Glossary
              </Button>
              <Button
                className="seo-action-btn seo-action-btn-secondary"
                icon={<ReloadOutlined />}
                onClick={fetchStatus}
              >
                Refresh
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <div className="seo-dashboard-content">
        {loading || !status ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <Spin size="large" tip="Loading dashboard..." />
          </div>
        ) : (
          <>
        {/* Status Alert */}
        {status?.config?.isPaused && (
          <Alert
            className="status-alert"
            message="Releases Paused"
            description={`Reason: ${status?.config?.pauseReason}`}
            type="warning"
            showIcon
            closable
            style={{ marginBottom: 24 }}
          />
        )}

        {/* Quick Actions */}
        <div className="quick-actions">
          <div
            className="quick-action-card"
            onClick={() => navigateTo("/seo/programmatic-seo/campaigns")}
          >
            <div className="quick-action-icon icon-rocket">
              <RocketOutlined />
            </div>
            <div className="quick-action-title">Create Campaign</div>
            <div className="quick-action-desc">Launch a new SEO campaign</div>
          </div>
          <div
            className="quick-action-card"
            onClick={() => navigateTo("/seo/programmatic-seo/analytics")}
          >
            <div className="quick-action-icon icon-check">
              <BarChartOutlined />
            </div>
            <div className="quick-action-title">View Analytics</div>
            <div className="quick-action-desc">Monitor performance</div>
          </div>
          <div
            className="quick-action-card"
            onClick={() => navigateTo("/seo/programmatic-seo/pages")}
          >
            <div className="quick-action-icon icon-file">
              <FileTextOutlined />
            </div>
            <div className="quick-action-title">Manage Pages</div>
            <div className="quick-action-desc">View generated pages</div>
          </div>
          <div
            className="quick-action-card"
            onClick={() => navigateTo("/seo/programmatic-seo/campaigns")}
          >
            <div className="quick-action-icon icon-clock">
              <CalendarOutlined />
            </div>
            <div className="quick-action-title">Schedule Campaign</div>
            <div className="quick-action-desc">Plan page generation</div>
          </div>
        </div>

        {/* Campaign Overview */}
        <Card className="campaign-overview-card" title="Campaign Overview">
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={12} md={6}>
              <div className="campaign-stat-item">
                <div
                  className="campaign-stat-value"
                  style={{ color: "#1e293b" }}
                >
                  {status.campaigns.total || 0}
                </div>
                <div className="campaign-stat-label">Total Campaigns</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div className="campaign-stat-item">
                <div
                  className="campaign-stat-value"
                  style={{ color: "#3b82f6" }}
                >
                  {status.campaigns.running || 0}
                </div>
                <div className="campaign-stat-label">Running</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div className="campaign-stat-item">
                <div
                  className="campaign-stat-value"
                  style={{ color: "#f59e0b" }}
                >
                  {status.campaigns.scheduled || 0}
                </div>
                <div className="campaign-stat-label">Scheduled</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div className="campaign-stat-item">
                <div
                  className="campaign-stat-value"
                  style={{ color: "#10b981" }}
                >
                  {status.campaigns.completed || 0}
                </div>
                <div className="campaign-stat-label">Completed</div>
              </div>
            </Col>
          </Row>
          <div className="card-actions">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigateTo("/seo/programmatic-seo/campaigns")}
            >
              Create New Campaign
            </Button>
          </div>
        </Card>

        {/* Statistics Row */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card className="stat-card">
              <Statistic
                title="Total Pages Generated"
                value={status?.stats?.totalPagesGenerated || 0}
                prefix={<FileTextOutlined className="icon-file" />}
                formatter={(value) => value.toLocaleString()}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card className="stat-card">
              <Statistic
                title="Success Rate"
                value={
                  status?.stats?.totalPagesGenerated > 0
                    ? (
                        (status.stats.totalPagesSuccessful /
                          status.stats.totalPagesGenerated) *
                        100
                      ).toFixed(1)
                    : 0
                }
                suffix="%"
                valueStyle={{ color: "#3f8600" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card className="stat-card">
              <Statistic
                title="Avg Quality Score"
                value={status?.stats?.avgQualityScore?.toFixed(1) || 0}
                suffix="/ 100"
                valueStyle={{
                  color:
                    (status?.stats?.avgQualityScore || 0) >= 70
                      ? "#3f8600"
                      : "#cf1322",
                }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card className="stat-card">
              <Statistic
                title="Queue Status"
                value={status?.queue?.pending || 0}
                suffix="pending"
                prefix={<ClockCircleOutlined className="icon-clock" />}
              />
            </Card>
          </Col>
        </Row>

        {/* Page Type Breakdown */}
        <Card title="Page Type Performance" style={{ marginBottom: 24 }}>
          <div className="page-type-grid">
            {Object.entries(status.pageCounts || {}).map(([type, data]) => {
              const typeDisplayNames = {
                comparison: "Comparison",
                integration: "Integration",
                location: "Location",
                useCase: "Use Case",
                locationUseCase: "Location + Use Case",
                integrationUseCase: "Integration + Use Case",
              };
              const displayName = typeDisplayNames[type] || type.charAt(0).toUpperCase() + type.slice(1);
              
              return (
              <div key={type} className="page-type-card">
                <h3>{displayName} Pages</h3>
                <div className="page-type-stats">
                  <div className="page-type-stat">
                    <div className="page-type-stat-value">
                      {data.count.toLocaleString()}
                    </div>
                    <div className="page-type-stat-label">Total Pages</div>
                  </div>
                  <div className="page-type-stat">
                    <div className="page-type-stat-value">
                      {data.avgQualityScore}%
                    </div>
                    <div className="page-type-stat-label">Avg Quality</div>
                  </div>
                </div>
                <Progress
                  className="quality-progress"
                  percent={parseFloat(data.avgQualityScore) || 0}
                  strokeColor={{
                    "0%": "#667eea",
                    "100%": "#764ba2",
                  }}
                />
              </div>
              );
            })}
          </div>
        </Card>

        {/* Recent Campaigns */}
        <Card title="Recent Campaigns" style={{ marginBottom: 24 }}>
          <div className="custom-table">
            {status?.recentReleases && status.recentReleases.length > 0 ? (
              <Table
                columns={campaignColumns}
                dataSource={status.recentReleases}
                rowKey="releaseNumber"
                pagination={false}
                size="middle"
              />
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "#64748b",
                }}
              >
                No campaigns created yet. Start by creating your first campaign!
              </div>
            )}
          </div>
        </Card>

        {/* System Status */}
        <Card title="System Status">
          <Descriptions bordered size="middle" className="seo-descriptions">
            <Descriptions.Item label="System Status">
              <Badge status="success" text="Campaign System Active" />
            </Descriptions.Item>
            <Descriptions.Item label="Active Campaigns">
              {status?.campaigns?.running || 0} running
            </Descriptions.Item>
            <Descriptions.Item label="Queue Status">
              {status?.queue?.pending || 0} items pending
            </Descriptions.Item>
            <Descriptions.Item label="Average Quality">
              {status?.stats?.avgQualityScore?.toFixed(1) || 0}%
            </Descriptions.Item>
            <Descriptions.Item label="Total Pages">
              {status?.stats?.totalPagesGenerated?.toLocaleString() || 0}
            </Descriptions.Item>
            <Descriptions.Item label="Success Rate">
              {status?.stats?.totalPagesGenerated > 0
                ? (
                    (status.stats.totalPagesSuccessful /
                      status.stats.totalPagesGenerated) *
                    100
                  ).toFixed(1)
                : 0}
              %
            </Descriptions.Item>
          </Descriptions>
        </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default ProgrammaticSeoDashboard;
